import React, { useState } from "react";
import { Button, Dialog, DialogContent, DialogActions, FormControl, InputLabel, Select, MenuItem, TextField, Box, Typography, LinearProgress } from "@mui/material";
import { createWithAiForMultipleValues, cancelCreation } from "../../../Utils/additionalInfo";
import dictionary from "../../../Mobx/Dictionary";
import { observer } from "mobx-react-lite";
import additionalInfo_parallelCreation from "../../../Mobx/AdditionalInfo_parallelCreation";
import AdditionalInfoStatusDialog from "./AdditionalInfoStatusDialog";

const AutoCreation = observer(() => {
    // state ---------------------------------------------------------------
    // For open and close dialog
    const [open, setOpen] = useState(false);
    const [open_parallelCreation, setOpen_parallelCreation] = useState(false);

    // For TextField
    const [type, setType] = useState("sentence");
    const [quantity, setQuantity] = useState(1);
    const [delayTime, setDelayTime] = useState(1);
    const [numOfProcesses, setNumOfProcesses] = useState(1);
    const dictionaryLength = dictionary.current.data[dictionary.current.dictionaryType].length;

    // For single process creation
    const [isCanceled, setIsCanceled] = useState(true);

    // Progress Bar calculation
    let total = dictionary.current.data[dictionary.current.dictionaryType].length;
    let done = dictionary.current.value.index;
    done = done === -1 ? 0 : done;
    let remaining = total - done;
    let progress = Math.max(0, (remaining / total) * 100);

    // functions -----------------------------------------------------------
    // Open and close dialog
    const handleOpen = () => setOpen(true);
    const handleClose = () => setOpen(false);

    // Handle the creation process
    const handleCreate = async () => {
        if (numOfProcesses === 1) { // Single process creation
            handleClose(); // Close the dialog
            setIsCanceled(false);
            if (type === "imageDescription")
                await createWithAiForMultipleValues("image", quantity, delayTime * 1000, true);
            else
                await createWithAiForMultipleValues(type, quantity, delayTime * 1000, false);
            setIsCanceled(true);
        } else { // Parallel creation
            if (type === "imageDescription")
                additionalInfo_parallelCreation.createMultipleProcesses("image", quantity, delayTime * 1000, true, numOfProcesses);
            else
                additionalInfo_parallelCreation.createMultipleProcesses(type, quantity, delayTime * 1000, false, numOfProcesses);
            setOpen_parallelCreation(true);
        }
    };

    // Cancel creation for single process
    const handleCancel = () => {
        if (numOfProcesses === 1) {
            cancelCreation();
            setIsCanceled(true);
        }
    };

    // render -------------------------------------------------------------
    return (
        <>
            {/* Progress Bar - Fixed at the Top */}
            {!isCanceled && (
                <Box sx={{ position: "fixed", top: 0, left: 0, width: "100%", zIndex: 1500 }}>
                    <LinearProgress variant="determinate" value={progress} />
                </Box>
            )}
            {!isCanceled && (
                <Box
                    sx={{
                        position: "fixed",
                        top: 8,
                        left: "13%",
                        transform: "translateX(-50%)",
                        zIndex: 1500,
                        bgcolor: "rgba(0, 0, 0, 1.5)",
                        color: "white",
                        py: 0.8,
                        px: 1.5,
                        borderRadius: "8px",
                        boxShadow: "0px 2px 5px rgba(0,0,0,0.2)",
                        fontWeight: 500,
                        minWidth: 320,
                        textAlign: "center",
                    }}
                >
                    <Typography variant="body2" sx={{ fontSize: "14px" }}>
                        {`בוצעו ${done}, נותרו ${remaining} מתוך ${total} (${progress.toFixed(2)}%)`}
                    </Typography>
                </Box>
            )}

            {isCanceled ? (
                <Button variant="contained" color="primary" onClick={handleOpen}>
                    יצירה אוטומטית
                </Button>
            ) : (
                <Button variant="contained" color="secondary" onClick={handleCancel} sx={{ minWidth: 118 }}>
                    ביטול יצירה
                </Button>
            )}

            <Dialog open={open} onClose={handleClose} maxWidth="xl" fullWidth>
                {/* <DialogTitle>בחירת סוג וכמות ליצירה</DialogTitle> */}
                <DialogContent>
                    {/* בחירת סוג וכמות ליצירה */}
                    <Box display="flex" gap={2} alignItems="center">
                        <FormControl fullWidth margin="dense" sx={{ width: '300px' }} variant="outlined">
                            <InputLabel id="select-label">מה ליצור</InputLabel>
                            <Select
                                labelId="select-label"
                                id="select"
                                value={type}
                                label="מה ליצור"
                                onChange={(e) => setType(e.target.value)}
                            >
                                <MenuItem value="image">תמונות ללא תיאור</MenuItem>
                                <MenuItem value="imageDescription">תמונות עם תיאור</MenuItem>
                                <MenuItem value="sentence">משפטים</MenuItem>
                                <MenuItem value="definition">הגדרות</MenuItem>
                                <MenuItem value="story">סיפורים</MenuItem>
                                <MenuItem value="trnsAr">תרגום לערבית</MenuItem>
                                <MenuItem value="trnsRu">תרגום לרוסית</MenuItem>
                                <MenuItem value="distractingForTrns">מסיחים עבור תרגום</MenuItem>
                                <MenuItem value="distractingForOriginal">מסיחים עבור אוריגינאל</MenuItem>
                                <MenuItem value="distractingForDefinitionInEng">מסיחים עבור הגדרות באנגלית</MenuItem>
                                <MenuItem value="distractingForDefinitionInHeb">מסיחים עבור הגדרות בעברית</MenuItem>
                                <MenuItem value="distractingForWordFamilies">מסיחים עבור משפחות מילים</MenuItem>
                                <MenuItem value="sentencesWithMissing">משפטים שחסר בהם מילה באמצע</MenuItem>
                                <MenuItem value="sentencesWithLastMissing">משפטים שחסר בהם מילה בסוף</MenuItem>
                                <MenuItem value="opposites">הפכים וזוגות</MenuItem>
                                <MenuItem value="synonyms">מילים נרדפות</MenuItem>
                            </Select>
                        </FormControl>

                        <TextField
                            label="כמה ליצור"
                            type="number"
                            fullWidth
                            margin="dense"
                            value={quantity}
                            onChange={(e) => setQuantity(Math.max(1, e.target.value))}
                            onKeyDown={(e) => {
                                if (e.key === "." || e.key === "," || e.key === "e") {
                                    e.preventDefault(); // מניעת הקלדת תווים עשרוניים או אקספוננציאליים
                                }
                            }}
                            inputProps={{ step: 1 }} // הגדרת שלב הקלט ל-1
                            sx={{ width: '100px' }}
                        />

                        <TextField
                            label="מספר תהליכים"
                            type="number"
                            fullWidth
                            margin="dense"
                            value={numOfProcesses}
                            onChange={(e) => {
                                const value = Math.max(1, e.target.value);
                                setNumOfProcesses(Math.min(value, dictionaryLength));
                            }}
                            onKeyDown={(e) => {
                                if (e.key === "." || e.key === "," || e.key === "e") {
                                    e.preventDefault(); // מניעת הקלדת תווים עשרוניים או אקספוננציאליים
                                }
                            }}
                            inputProps={{ step: 1 }} // הגדרת שלב הקלט ל-1
                            sx={{ width: '180px' }}
                        />

                        <TextField
                            label="זמן המתנה בין פעולות (בשניות)"
                            type="number"
                            fullWidth
                            margin="dense"
                            value={delayTime}
                            onChange={(e) => setDelayTime(Math.max(1, e.target.value))}
                            sx={{ width: '180px' }}
                        />
                    </Box>

                    {/* הוספת ההערות */}
                    <Box mt={2} p={2} bgcolor="#f8f8f8" borderRadius={2}>
                        <Typography variant="body2" color="textSecondary">
                            <strong>שים לב:</strong>
                            <ul>
                                <li>ניתן לבצע <strong>סינון/חיפוש</strong>, כדי ליצור עבור מילים מסויימות.</li>
                                <li><strong>בתהליך בודד</strong> - המעקב אחר היצירה יהיה באזור היצירה הידנית באתר, <strong>בתהליכים מרובים</strong> - יפתח חלון חדש לצורך מעקב אחר כל התהליכים.</li>
                                <li>יש <strong>לבטל מצב שינה</strong> במחשב, כדי שהתהליך לא יפסק באמצע.</li>
                                <li>יש <strong>לחבר את המחשב לחשמל</strong> או לוודא שיש מספיק סוללה.</li>
                                <li>יש להשאיר את המחשב <strong>מחובר לאינטרנט</strong>.</li>
                                <li>יש לוודא שיש מספיק <strong>אשראי </strong>בספק ה-AI.</li>
                                <li>
                                    יש מילים שה-AI מונע ליצור עבורן תמונות (מטעמי בטיחות). במקרה כזה תוצג הודעת שגיאה* ולא תיווצר תמונה עבור אותה מילה.
                                </li>
                            </ul>
                            <Typography variant="body2" color="textSecondary" sx={{ direction: "rtl" }}>
                                *"Your request was rejected as a result of our safety system. Your prompt may contain text that is not allowed by our safety system".
                            </Typography>
                        </Typography>
                    </Box>
                </DialogContent>

                {/* כפתורי פעולה */}
                <DialogActions sx={{ display: "flex", padding: 2 }}>
                    <Button onClick={handleCreate} color="primary" variant="contained">
                        צור
                    </Button>
                    <Button onClick={handleClose} color="secondary" variant="outlined">
                        סגור
                    </Button>
                </DialogActions>
            </Dialog>

            <AdditionalInfoStatusDialog open={open_parallelCreation} onClose={() => setOpen_parallelCreation(false)} />
        </>
    );
});

export default AutoCreation;